import type { ChangeEvent, JSX } from 'react';
import { Input } from './Input';

/**
 * Configuration for a single form field
 */
export interface FieldConfig<T> {
  /**
   * Display label for the field
   */
  label: string;

  /**
   * Key in the data object
   */
  key: keyof T;

  /**
   * Input type
   */
  type: 'number' | 'text';

  /**
   * Optional prefix to display (e.g., "£" for currency)
   */
  prefix?: string;

  /**
   * Optional helper text to display below the field
   */
  helperText?: string;

  /**
   * Optional validation function that returns an error message if invalid
   */
  validate?: (value: string | number) => string | undefined;

  /**
   * Optional default value when the data value is undefined
   */
  defaultValue?: string | number;
}

export interface FormFieldSetProps<T> {
  /**
   * Array of field configurations
   */
  fields: Array<FieldConfig<T>>;

  /**
   * Current form data
   */
  data: T;

  /**
   * Callback when a field value changes
   */
  onChange: (key: keyof T, value: string | number) => void;

  /**
   * Optional object mapping field keys to error messages
   */
  errors?: Partial<Record<keyof T, string>>;
}

/**
 * FormFieldSet component - renders a set of form fields based on configuration
 *
 * Features:
 * - Declarative field configuration
 * - Automatic handling of number vs text conversion
 * - Consistent grid layout
 * - Full-width inputs by default
 *
 * @example
 * ```tsx
 * const fields = [
 *   {
 *     label: "Gross Salary",
 *     key: "gross_salary",
 *     type: "number",
 *     prefix: "£",
 *     helperText: "Your annual gross salary"
 *   },
 *   { label: "Tax Code", key: "tax_code", type: "text" },
 * ];
 *
 * <FormFieldSet
 *   fields={fields}
 *   data={formData}
 *   onChange={(key, value) => setFormData({ ...formData, [key]: value })}
 * />
 * ```
 */
export function FormFieldSet<T>({ fields, data, onChange, errors }: FormFieldSetProps<T>): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4">
      {fields.map(({ label, key, type, prefix, helperText, validate, defaultValue }) => {
        // Get error from either explicit errors prop or field validation
        // Only validate non-empty values to avoid showing errors before user input
        const rawValue = data[key] as string | number | undefined;
        const value = rawValue ?? defaultValue ?? (type === 'number' ? '' : '');
        const hasValue =
          value !== null &&
          value !== undefined &&
          (typeof value === 'number' || (typeof value === 'string' && value.trim() !== ''));
        const error = errors?.[key] ?? (validate && hasValue ? validate(value) : undefined);

        return (
          <Input
            key={String(key)}
            type={type}
            value={value}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange(key, type === 'number' ? Number(e.target.value) : e.target.value)
            }
            label={label}
            prefix={prefix}
            helperText={helperText}
            error={error}
            fullWidth
            selectAllOnFocus={type === 'number'}
          />
        );
      })}
    </div>
  );
}
